import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { AgentObject } from "@/lib/api";
import { AppChrome } from "@/components/Chrome";
import { Sigil, StatusRing } from "@/components/Sigil";
import { TruncatedAddr, SOLSCAN } from "@/components/Primitives";
import { ChainBadge } from "@/components/ChainBadge";
import { useAuth } from "@/components/providers/AuthProvider";
import { api } from "@/lib/api";

export function DashboardPage() {
  const { token } = useAuth();
  const [agents, setAgents] = useState<AgentObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<AgentObject | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api
      .listAgents(token)
      .then((res) => {
        setAgents(res.agents);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const counts = {
    total: agents.length,
    active: agents.filter((a) => a.status === "active" && !a.is_pending).length,
    paused: agents.filter((a) => a.status === "paused" && !a.is_pending).length,
    revoked: agents.filter((a) => a.status === "revoked").length,
    pending: agents.filter((a) => a.is_pending).length,
  };

  const onConfirmCancel = async () => {
    if (!token || !confirmCancel) return;
    const id = confirmCancel.on_chain_pubkey || confirmCancel.id;
    setCancelling(true);
    try {
      await api.cancelPendingAgent(id, token);
      setAgents((prev) => prev.filter((a) => a.id !== id && a.on_chain_pubkey !== id));
      setConfirmCancel(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCancelling(false);
    }
  };

  const hasAgents = agents.length > 0;
  const showFleetChrome = hasAgents || loading;

  return (
    <AppChrome>
      <div className={`page page--dashboard ${!hasAgents && !loading ? "page--dashboard-empty" : ""}`}>
        {showFleetChrome && (
          <>
            <div className="page__head">
              <div>
                <span className="kicker">Fleet overview</span>
                <h1 className="page__title">Agents</h1>
              </div>
              <Link to="/agents/new" className="btn btn--primary">+ Register agent</Link>
            </div>

            <section className="stat-row">
              <StatBlock label="Total" value={counts.total} />
              <StatBlock label="Active" value={counts.active} />
              <StatBlock label="Paused" value={counts.paused} tone="muted" />
              <StatBlock label="Revoked" value={counts.revoked} tone="oxblood" />
              {counts.pending > 0 && <StatBlock label="Pending" value={counts.pending} tone="bronze" />}
            </section>
          </>
        )}

        {error && <div className="alert">Failed to load agents: {error}</div>}

        {loading ? (
          <div className="loading-skeleton">
            <span className="kicker">Loading the chronicle…</span>
          </div>
        ) : hasAgents ? (
          <ul className="agent-grid">
            {agents.map((a) => (
              <AgentCard key={a.id} agent={a} onRequestCancel={setConfirmCancel} />
            ))}
          </ul>
        ) : (
          <EmptyState />
        )}
      </div>

      {confirmCancel && (
        <CancelPendingModal
          agent={confirmCancel}
          cancelling={cancelling}
          onClose={() => { if (!cancelling) setConfirmCancel(null); }}
          onConfirm={() => void onConfirmCancel()}
        />
      )}

      <DashboardStyles />
    </AppChrome>
  );
}

function CancelPendingModal({
  agent,
  cancelling,
  onClose,
  onConfirm,
}: {
  agent: AgentObject;
  cancelling: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="dash-modal-bg" onClick={cancelling ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="cancel-title">
      <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
        <span className="kicker kicker--oxblood">⊗ Cancel pending registration</span>
        <h2 id="cancel-title" className="dash-modal__title">Cancel <em>{agent.name}</em>?</h2>
        <p className="dash-modal__body">
          Drops the row from your dashboard and <strong>invalidates its API key immediately</strong>. If you already signed the on-chain register tx, it will still land — but the agent won't appear here, and the SDK can't authenticate.
        </p>
        <div className="dash-modal__actions">
          <button className="btn btn--ghost" onClick={onClose} disabled={cancelling}>Keep waiting</button>
          <button className="btn btn--destructive" onClick={onConfirm} disabled={cancelling}>
            {cancelling ? "Cancelling…" : "Confirm cancel →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value, tone }: { label: string; value: number; tone?: "muted" | "oxblood" | "bronze" }) {
  return (
    <div className="stat">
      <span className="kicker">{label}</span>
      <div className={`stat__value num ${tone === "oxblood" ? "stat__value--oxblood" : ""} ${tone === "muted" ? "stat__value--muted" : ""} ${tone === "bronze" ? "stat__value--bronze" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="empty-state">
      <div className="empty-state__sigil">
        <Sigil seed="empty-watching" size={140} tone="bone" glow sweeping strokeWidth={1.1} />
      </div>
      <span className="kicker kicker--bronze empty-state__kicker">⊙ Watching · no agents yet</span>
      <h2 className="empty-state__headline">The chronicle is empty.</h2>
      <p className="empty-state__body">
        One transaction provisions a Swig session-key with caps, scope, and a kill-switch. ~10 seconds.
      </p>
      <Link to="/agents/new" className="btn btn--primary empty-state__cta">
        Register your first agent →
      </Link>
    </div>
  );
}

function AgentCard({ agent, onRequestCancel }: { agent: AgentObject; onRequestCancel: (a: AgentObject) => void }) {
  const isPending = agent.is_pending === true;
  const ageMs = Date.now() - new Date(agent.created_at).getTime();
  const isStalePending = isPending && ageMs > 10 * 60 * 1000; // 10 minutes
  return (
    <li className={`agent-card ${isPending ? "agent-card--pending" : ""}`}>
      <Link to={`/agents/${agent.on_chain_pubkey || agent.id}`} className="agent-card__link">
        <div className="agent-card__sigil">
          <Sigil
            seed={agent.on_chain_pubkey || agent.id}
            size={72}
            tone="bone"
            cracked={agent.status === "revoked" || agent.status === "archived"}
            sweeping={agent.status === "active" && !isPending}
            strokeWidth={1.1}
          />
        </div>
        <div className="agent-card__body">
          <div className="agent-card__statusrow">
            {isPending ? (
              <>
                <span className="agent-card__pending-dot" />
                <span className="kicker" style={{ color: "var(--bronze)" }}>
                  {isStalePending ? "REGISTRATION TIMED OUT" : "REGISTRATION PENDING"}
                </span>
              </>
            ) : (
              <>
                <StatusRing status={agent.status as "active" | "paused" | "revoked" | "archived"} size={11} />
                <span className="kicker" style={{ color: agent.status === "revoked" ? "var(--oxblood)" : "var(--bone)" }}>
                  {agent.status.toUpperCase()}
                </span>
              </>
            )}
          </div>
          <h3 className="agent-card__name">{agent.name}</h3>
          <div className="agent-card__meta" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span>{frameworkLabel(agent.framework)}</span>
            <ChainBadge chain={agent.chain ?? "solana"} />
          </div>
        </div>
      </Link>
      {isPending ? (
        <div className="agent-card__pending-actions">
          <span className="agent-card__pending-help">
            {isStalePending
              ? "On-chain register tx never confirmed. Cancel to remove this row + invalidate its API key."
              : "Awaiting on-chain confirmation. Sign the tx in your wallet, or cancel below."}
          </span>
          <button
            className="btn btn--destructive btn--small"
            onClick={(e) => {
              e.preventDefault();
              onRequestCancel(agent);
            }}
          >
            Cancel registration
          </button>
        </div>
      ) : (
        <div className="agent-card__refs">
          <TruncatedAddr
            addr={agent.on_chain_pubkey}
            href={SOLSCAN(agent.on_chain_pubkey)}
            label="PDA"
          />
          {agent.substrate_account_pubkey && (
            <TruncatedAddr
              addr={agent.substrate_account_pubkey}
              href={SOLSCAN(agent.substrate_account_pubkey)}
              label="Swig"
            />
          )}
        </div>
      )}
    </li>
  );
}

function frameworkLabel(f: string): string {
  switch (f) {
    case "sak": return "Solana Agent Kit";
    case "mcp": return "MCP";
    case "eliza": return "ElizaOS";
    case "virtuals": return "Virtuals";
    case "griffain": return "Griffain";
    default: return f;
  }
}

function DashboardStyles() {
  return (
    <style>{`
      .page__head { display: flex; align-items: end; justify-content: space-between; margin-bottom: var(--s-6); }
      .page__title { font: var(--type-display-page); margin: var(--s-2) 0 0; }
      /* auto-fit handles 4 vs 5 stat blocks (Pending appears conditionally) without
         awkward 4+1 wrap; min 140px keeps numbers readable on tablets. */
      .stat-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: var(--s-4); margin-bottom: var(--s-7); }
      .stat { padding: var(--s-4); background: var(--bg-elev); border: 1px solid var(--line-soft); }
      .stat__value { font: var(--type-display-section); font-size: 36px; margin-top: var(--s-2); color: var(--bone); }
      .stat__value--oxblood { color: var(--oxblood); }
      .stat__value--muted { color: var(--slate-2); }
      .stat__value--bronze { color: var(--bronze); }
      .alert { padding: var(--s-3) var(--s-4); background: var(--bg-alert); border-left: 2px solid var(--oxblood); color: var(--oxblood); margin-bottom: var(--s-5); font: var(--type-body); }
      .agent-grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: var(--s-4); }
      .agent-card { background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-4); }
      .agent-card--pending { border-left: 3px solid var(--bronze); }
      .agent-card__pending-dot {
        width: 8px; height: 8px; border-radius: 999px;
        background: var(--bronze);
        animation: agent-pending-pulse 1.6s ease-in-out infinite;
        display: inline-block;
      }
      @keyframes agent-pending-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
      .agent-card__pending-actions {
        margin-top: var(--s-3); padding-top: var(--s-3);
        border-top: 1px solid var(--line-soft);
        display: flex; flex-direction: column; gap: var(--s-2);
      }
      .agent-card__pending-help { font: var(--type-body-small); color: var(--slate); }
      .btn--small { padding: 6px 14px; font-size: 11px; }
      .agent-card__link { display: grid; grid-template-columns: auto 1fr; gap: var(--s-4); }
      .agent-card__sigil { display: flex; align-items: center; }
      .agent-card__body { display: flex; flex-direction: column; gap: var(--s-1); }
      .agent-card__statusrow { display: inline-flex; align-items: center; gap: var(--s-2); font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; }
      .agent-card__name { font: var(--type-display-section); font-style: italic; margin: var(--s-1) 0; }
      .agent-card__meta { color: var(--slate); font: var(--type-body-small); display: flex; gap: var(--s-2); }
      .agent-card__refs { display: flex; gap: var(--s-3); flex-wrap: wrap; padding-top: var(--s-3); border-top: 1px dashed var(--line-soft); margin-top: var(--s-3); }
      /* Empty-state: when total === 0 we hide the fleet chrome (head + stat row)
         and let the empty state own the available viewport. The page__main
         padding (s-6 top + s-6 bottom) is already accounted for; we minus the
         topbar (~72px) + footer (~72px) to keep the cluster vertically centered
         without overflow. */
      .page--dashboard-empty { min-height: calc(100vh - 72px - 72px - var(--s-6) - var(--s-6)); display: flex; align-items: center; justify-content: center; }
      .empty-state { text-align: center; display: flex; flex-direction: column; align-items: center; gap: var(--s-3); max-width: 540px; }
      .empty-state__sigil {
        margin-bottom: var(--s-3);
        display: inline-block;
        animation:
          empty-state-burst 1800ms cubic-bezier(.4, 0, .2, 1) 200ms 1 forwards,
          empty-state-breathe 4s ease-in-out 2000ms infinite;
        will-change: transform, filter;
      }
      @keyframes empty-state-burst {
        0%   { transform: scale(0.92); filter: drop-shadow(0 0 0 transparent); opacity: 0; }
        45%  { transform: scale(1.06); filter: drop-shadow(0 0 28px var(--bronze-glow)); opacity: 1; }
        100% { transform: scale(1);    filter: drop-shadow(0 0 0 transparent); opacity: 1; }
      }
      @keyframes empty-state-breathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.025); }
      }
      .empty-state__kicker { margin-top: var(--s-2); }
      .empty-state__headline { font: var(--type-display-section); font-size: 32px; margin: var(--s-2) 0; }
      .empty-state__body { color: var(--slate); max-width: 460px; font: var(--type-body-large); }
      .empty-state__cta { margin-top: var(--s-4); padding: var(--s-3) var(--s-6); }
      .btn { display: inline-flex; align-items: center; padding: var(--s-3) var(--s-5); font: var(--type-label); letter-spacing: var(--tracking-label); text-transform: uppercase; }
      .btn--primary { background: var(--bronze); color: var(--text-on-ink); }
      .btn--primary:hover { background: var(--bronze-deep); }
      .btn--ghost { background: transparent; color: var(--bone); border: 1px solid var(--line); }
      .btn--ghost:hover { border-color: var(--bone); }
      .btn--destructive { background: var(--oxblood); color: var(--text-on-ink); }
      .btn--destructive:hover { background: var(--oxblood-bright); }
      .dot-sep { color: var(--slate-3); }
      .loading-skeleton { padding: var(--s-7); text-align: center; }
      .dash-modal-bg { position: fixed; inset: 0; background: rgba(13, 11, 8, 0.45); display: flex; align-items: center; justify-content: center; z-index: 100; padding: var(--s-4); }
      .dash-modal { background: var(--bg-elev2); border: 1px solid var(--line); padding: var(--s-6); max-width: 480px; width: 100%; max-height: 92vh; overflow-y: auto; box-shadow: 0 24px 64px rgba(13, 11, 8, 0.18); display: flex; flex-direction: column; gap: var(--s-3); }
      .dash-modal__title { font-family: var(--font-display); font-style: italic; font-size: 26px; margin: var(--s-2) 0; }
      .dash-modal__body { font: var(--type-body); color: var(--slate); margin: 0; }
      .dash-modal__body strong { color: var(--oxblood); }
      .dash-modal__actions { display: flex; justify-content: flex-end; gap: var(--s-3); padding-top: var(--s-3); border-top: 1px solid var(--line-soft); margin-top: var(--s-2); }

      @media (max-width: 768px) {
        .stat-row { grid-template-columns: repeat(2, 1fr); }
      }
    `}</style>
  );
}
